import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AdvisorProvider } from '@/lib/advisor-context';
import { BuyerAuthProvider } from '@/lib/buyer-auth-context';
import { AdvisorPage } from '@/app/advisor-page';
import { RevealPage } from '@/app/reveal-page';
import { clearAdvisorSession } from '@/lib/session';
import './globals.css';
import './prototype.css';

/**
 * `?fresh=1` starts a genuinely new line before anything hydrates.
 *
 * The message list, the brief, the shortlist and the session id all live in
 * localStorage under fixed keys, and AdvisorProvider reads them while it
 * initialises — so a second visit continues the first one. That is right for
 * a buyer and wrong for anyone opening the app repeatedly as different people:
 * chat two inherits chat one's brief and board, and every run
 * after the first is measuring the wrong thing.
 *
 * It has to run HERE, not in a route effect. By the time a component's effect
 * fires, the provider has already hydrated the old state.
 *
 * Pairs with `?q=` — `/chat?fresh=1&q=…` is a clean session that opens with
 * that message: one URL, no API, the real UI.
 */
if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('fresh')) {
  clearAdvisorSession();
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BuyerAuthProvider>
      <AdvisorProvider>
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<AdvisorPage />} />
            <Route path="/chat" element={<AdvisorPage />} />
            <Route path="/reveal" element={<RevealPage />} />
            <Route path="*" element={<AdvisorPage />} />
          </Routes>
        </BrowserRouter>
      </AdvisorProvider>
    </BuyerAuthProvider>
  </StrictMode>,
);

// PWA: installable from the browser menu; opens standalone into /chat.
// Registration is best-effort — the app never depends on the SW.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}
